// HMAC-SHA256 签名工具
// 算法参考后端签名文档：value1&value2&value3（只取参数值，HMAC-SHA256）

const enc = new TextEncoder()
const INTEGRITY_HASH_HEX_LENGTH = 16

/**
 * 生成 8 位随机字符串（小写字母 + 数字）
 * 规范：`docs/v5/modules/sign-hmacsha256.md` 3.2 — 字符集 `[a-z0-9]`
 */
export function generateNonce(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  const arr = new Uint8Array(8)
  crypto.getRandomValues(arr)
  let s = ''
  for (let i = 0; i < arr.length; i++) {
    s += chars[arr[i] % chars.length]
  }
  return s
}

function bytesToHex(buf: ArrayBuffer): string {
  const view = new Uint8Array(buf)
  let out = ''
  for (let i = 0; i < view.length; i++) {
    out += view[i].toString(16).padStart(2, '0')
  }
  return out
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

/**
 * 加载 APP Secret（HMAC 密钥）
 *
 * 4 段 Base64 拆分常量必须完整（任一缺失 throw）；
 * atob 解码失败 throw（构建产物可能被篡改）。
 *
 * 完整性自检不在此函数中（异步 SHA-256 在 sign() 首次调用时执行）。
 *
 * 详见 docs/v5/specs/app-secret-management.md
 */
export function loadSecret(): string {
  const a = typeof __KEY_CONFIG_A__ === 'string' ? __KEY_CONFIG_A__ : ''
  const b = typeof __KEY_CONFIG_B__ === 'string' ? __KEY_CONFIG_B__ : ''
  const c = typeof __KEY_CONFIG_C__ === 'string' ? __KEY_CONFIG_C__ : ''
  const d = typeof __KEY_CONFIG_D__ === 'string' ? __KEY_CONFIG_D__ : ''

  if (!a || !b || !c || !d) {
    throw new Error(
      '[YFB][sign] APP Secret 拆分常量缺失：构建期密钥注入失败（请检查 VITE_HMAC_SECRET_WEB 是否配置）。' +
        '签名链路已阻断，请检查 .env 配置后重新构建。'
    )
  }

  let key: string
  try {
    key = atob(a) + atob(b) + atob(c) + atob(d)
  } catch {
    throw new Error(
      '[YFB][sign] APP Secret 拆分常量解码失败：构建产物可能被篡改或 vite.config.ts 与 sign.ts 不一致。' +
        '签名链路已阻断。'
    )
  }

  return key
}

/**
 * 拼接签名串（HMAC-SHA256 专用）：
 *   1. 按参数名字典序升序排列
 *   2. **只取参数值**（不含 key），用 & 连接
 *   3. 不做 URL 编码（后端规范不要求）
 *   4. 不追加 appsecret（密钥仅作 HMAC key 使用）
 */
export function buildSignString(params: Record<string, unknown>): string {
  return Object.keys(params)
    .sort()
    .map((k) => {
      const v = params[k]
      if (v === null || v === undefined) return ''
      return String(v)
    })
    .filter((s) => s !== '')
    .join('&')
}

/**
 * 启动期完整性自检（首次 sign() 调用时执行，结果缓存）
 *
 * 校验密钥的 SHA-256 前 8 字节 hex（16 字符）是否与构建期注入的
 * __KEY_INTEGRITY_HASH__ 一致。防御场景：dist 被手动 patch 后，
 * 攻击者需同步修改 4 段拆分常量与本常量才能通过自检，留下一致性证据。
 *
 * __KEY_INTEGRITY_HASH__ 本身公开无风险（无法逆向密钥）。
 *
 * 详见 docs/v5/specs/app-secret-management.md §3.7
 */
let integrityChecked = false
async function ensureIntegrity(secretBase64: string): Promise<void> {
  if (integrityChecked) return
  if (typeof __KEY_INTEGRITY_HASH__ !== 'string' || !__KEY_INTEGRITY_HASH__) {
    // 开发构建或 vitest 环境未注入完整性哈希：跳过自检（仅防御纵深缺失，非主防线）
    integrityChecked = true
    return
  }
  if (__KEY_INTEGRITY_HASH__.length !== INTEGRITY_HASH_HEX_LENGTH) {
    throw new Error(
      `[YFB][sign] __KEY_INTEGRITY_HASH__ 长度异常（期望 ${INTEGRITY_HASH_HEX_LENGTH} 字符 hex，实际 ${__KEY_INTEGRITY_HASH__.length}）。构建产物可能损坏。`
    )
  }
  const keyBytes = enc.encode(secretBase64)
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyBytes)
  const hashHex = bytesToHex(hashBuffer)
  if (hashHex.slice(0, INTEGRITY_HASH_HEX_LENGTH) !== __KEY_INTEGRITY_HASH__) {
    throw new Error(
      '[YFB][sign] APP Secret 完整性自检失败：密钥与构建期哈希不一致，构建产物可能被篡改。' +
        '签名链路已阻断。'
    )
  }
  integrityChecked = true
}

/**
 * 计算 HMAC-SHA256 签名
 *
 * 拼接顺序：
 *   1. 收集所有签名参数（业务参数 + 公共头参数 appkey/timestamp/nonce/version）
 *   2. 按参数名字典序升序排列
 *   3. **只取参数值**，用 & 连接（不包含参数名）
 *   4. 用 HMAC-SHA256 对拼接字符串做签名，密钥 = Base64 解码后的 32 字节二进制
 *   5. 输出 64 位小写 hex
 *
 * @param params 待签名参数
 * @param secretBase64 Base64 编码的密钥（32 字节 → 44 字符）
 * @returns 64 位小写 hex
 * @throws 密钥为空 / 完整性自检失败 / atob 解码失败时 throw
 */
export async function sign(
  params: Record<string, unknown>,
  secretBase64: string
): Promise<string> {
  if (!secretBase64) {
    throw new Error('[YFB][sign] secretBase64 为空，签名链路已阻断。')
  }

  const signStr = buildSignString(params)
  // 调试日志只在开发构建保留；生产构建中 esbuild 折叠掉整个 if 块。
  // 双重保险：vite.config.ts 已配置 drop: ['console']，但显式守门更确定性。
  if (__YFB_DEV__) {
    console.log('[YFB][sign] 拼接字符串:', signStr)
  }

  let keyBytes: Uint8Array
  try {
    keyBytes = base64ToBytes(secretBase64)
  } catch {
    throw new Error('[YFB][sign] secretBase64 解码失败，签名链路已阻断。')
  }

  // 启动期完整性自检（首次调用时执行，结果缓存）
  await ensureIntegrity(secretBase64)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as unknown as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const data = enc.encode(signStr)
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data as unknown as BufferSource)
  return bytesToHex(signature)
}
